import { NavigationContext } from 'expo-router/react-navigation';
import {
  Fragment,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  useMemo,
  type ComponentType,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Platform,
  type FlatListProps,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollViewProps,
  type ViewToken,
} from 'react-native';

import { FlatList } from '@/components/ui/flat-list';
import { ScrollView } from '@/components/ui/scroll-view';
import { VStack } from '@/components/ui/vstack';

/**
 * The props a scene hands its tab root's shared list: a FlatList's own, plus the ref the
 * scene keeps to it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each scene lists its own item type
type SceneListProps = FlatListProps<any> & { ref?: Ref<FlatList<any>> };

/**
 * Where a tab root's scenes leave their list for the shared one to draw. A scene writes on
 * every render and only the shared list subscribes, so a write never re-renders a scene.
 */
class SceneListRegistry {
  private lists = new Map<string, SceneListProps>();

  private listeners = new Set<() => void>();

  /** Set by the shared list to scroll to its top under the large title. */
  scrollToTop: (() => void) | undefined;

  set(key: string, props: SceneListProps | undefined) {
    if (props) {
      this.lists.set(key, props);
    } else {
      this.lists.delete(key);
    }

    this.listeners.forEach((listener) => listener());
  }

  get(key: string) {
    return this.lists.get(key);
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };
}

type TabRootSceneValue = {
  registry: SceneListRegistry;
  sceneKey: string;
};

const TabRootSceneContext = createContext<TabRootSceneValue | undefined>(undefined);

/** Whether this renders inside a tab root, where the tab root draws the list and its lead. */
export function useIsTabRoot(): boolean {
  return useContext(TabRootSceneContext) !== undefined;
}

const TabRootSceneSelectedContext = createContext(true);

/**
 * Whether the scene this is rendered in is the one the reader is looking at.
 *
 * A tab root keeps the scenes a reader has visited mounted, and the screen they share stays
 * focused the whole time, so screen focus cannot tell a hidden scene from the visible one.
 * Anything a scene raises over the whole screen, such as a modal, has to ask this as well, or
 * it opens over a different section. True outside a tab root, where there is only one scene.
 */
export function useIsTabRootSceneSelected(): boolean {
  return useContext(TabRootSceneSelectedContext);
}

/**
 * A scene's list. Outside a tab root it is a FlatList. Inside one it draws nothing itself and
 * hands its props to the tab root's shared list, which shows them while its scene is selected.
 *
 * The rows, header and empty state then render under the shared list, outside the scene's
 * own tree, so they cannot read a context the scene provides around this list. Provide it
 * inside each row or header instead.
 */
export function TabRootFlatList<ItemT>(
  props: FlatListProps<ItemT> & { ref?: Ref<FlatList<ItemT>> }
) {
  const scene = useContext(TabRootSceneContext);

  useLayoutEffect(() => {
    scene?.registry.set(scene.sceneKey, props as SceneListProps);
  });

  useLayoutEffect(() => {
    if (!scene) {
      return;
    }

    return () => scene.registry.set(scene.sceneKey, undefined);
  }, [scene]);

  if (scene) {
    return null;
  }

  return <FlatList {...props} />;
}

/**
 * A scene whose content is one block rather than rows. Inside a tab root the block fills the
 * shared list under the lead, with the scroll view's own props, refresh control included.
 */
export function TabRootScrollView({ children, ...props }: TabRootScrollViewProps) {
  const scene = useContext(TabRootSceneContext);

  if (!scene) {
    return <ScrollView {...props}>{children}</ScrollView>;
  }

  return (
    <TabRootFlatList
      {...(props as Omit<FlatListProps<never>, 'data' | 'renderItem'>)}
      data={NO_ROWS}
      renderItem={renderNoRow}
      ListEmptyComponent={<VStack className="grow">{children}</VStack>}
    />
  );
}

/**
 * For a list's loading, error and empty states, which render something other than the list.
 *
 * Without this, those states would drop the lead with the list, taking the segment control
 * with it and stranding the reader on an empty section with no way to switch. On a tab root
 * the state fills the shared list under the lead. Anywhere else it renders untouched.
 */
export function TabRootListFallback({ children }: { children: ReactNode }) {
  const scene = useContext(TabRootSceneContext);

  if (!scene) {
    return children;
  }

  return (
    <TabRootFlatList
      className="flex-1"
      contentContainerClassName="grow"
      showsVerticalScrollIndicator={false}
      data={NO_ROWS}
      renderItem={renderNoRow}
      ListEmptyComponent={<VStack className="grow">{children}</VStack>}
    />
  );
}

type TabRootScrollViewProps = ScrollViewProps & {
  className?: string;
  contentContainerClassName?: string;
  children: ReactNode;
};

const NO_ROWS: never[] = [];

const renderNoRow = () => null;

/**
 * What a tab root's `ref` offers the screen above it.
 *
 * `scrollToTop` goes to the top under the large title. Offset zero is not that top: the top
 * sits above the list's inset, and zero leaves the lead, banner and segment control under the
 * bar. Before the reader first drags the top may not be known yet, and the list is already
 * resting there, so it does nothing.
 */
export type TabRootScenesHandle = {
  scrollToTop: () => void;
};

type TabRootScenesProps<Key extends string> = {
  ref?: Ref<TabRootScenesHandle>;
  sceneKeys: readonly Key[];
  selectedKey: Key;
  lead: ReactNode;
  renderScene: (key: Key) => ReactNode;
};

/**
 * Shows one of a tab root's sections at a time, through one list.
 *
 * iOS collapses a large title against one scroll view, the first one down the screen's first
 * children, and offers no way to track several. A list per section, swapped by moving the
 * selected one first, made UIKit re-adjust both lists on every switch: the one being left
 * shifted as it stopped being tracked, and the one arriving jumped as the title resized. So
 * every section draws through the same native list, which UIKit never stops tracking.
 *
 * Each section is still its own component, mounted the first time it is selected and kept
 * mounted, so its queries, search and view events survive a switch. It hands its rows to the
 * shared list through `TabRootFlatList` rather than drawing them.
 */
export function TabRootScenes<Key extends string>({
  ref,
  sceneKeys,
  selectedKey,
  lead,
  renderScene,
}: TabRootScenesProps<Key>) {
  const [visitedKeys, setVisitedKeys] = useState<ReadonlySet<Key>>(() => new Set([selectedKey]));

  const [registry] = useState(() => new SceneListRegistry());

  useImperativeHandle(ref, () => ({ scrollToTop: () => registry.scrollToTop?.() }), [registry]);

  if (!visitedKeys.has(selectedKey)) {
    setVisitedKeys(new Set([...visitedKeys, selectedKey]));
  }

  return (
    <VStack className="flex-1">
      <SharedList registry={registry} selectedKey={selectedKey} lead={lead} />

      {sceneKeys
        .filter((key) => visitedKeys.has(key))
        .map((key) => (
          <TabRootScene
            key={key}
            registry={registry}
            sceneKey={key}
            isSelected={key === selectedKey}>
            {renderScene(key)}
          </TabRootScene>
        ))}
    </VStack>
  );
}

function TabRootScene({
  registry,
  sceneKey,
  isSelected,
  children,
}: TabRootSceneValue & { isSelected: boolean; children: ReactNode }) {
  const value = useMemo(() => ({ registry, sceneKey }), [registry, sceneKey]);

  return (
    <TabRootSceneSelectedContext.Provider value={isSelected}>
      <TabRootSceneContext.Provider value={value}>{children}</TabRootSceneContext.Provider>
    </TabRootSceneSelectedContext.Provider>
  );
}

/** The key FlatList gives a row when the scene sets no `keyExtractor`. */
function defaultKeyExtractor(item: unknown, index: number): string {
  const row = item as { key?: unknown; id?: unknown } | null;

  if (typeof row?.key === 'string') {
    return row.key;
  }

  if (typeof row?.id === 'string') {
    return row.id;
  }

  return String(index);
}

const KEY_SEPARATOR = ':';

/** How many times a restore is re-applied before the list's own answer is accepted. */
const MAX_RESTORE_ATTEMPTS = 5;

function renderHeader(header: SceneListProps['ListHeaderComponent']) {
  if (!header) {
    return null;
  }

  if (isValidElement(header)) {
    return header as ReactElement;
  }

  const Header = header as ComponentType;

  return <Header />;
}

function assignRef<Node>(ref: Ref<Node> | undefined, node: Node | null) {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    ref.current = node;
  }
}

/**
 * The one list a tab root draws, showing whichever section is selected.
 *
 * It remembers each section's offset and goes back to it on a switch, or to the top for a
 * section not shown before. The top under a large title sits above the list's own inset, so
 * the list sets `scrollToOverflowEnabled`, or Fabric would clamp a restore there to zero. iOS
 * reports that top in the scroll event UIKit sends when it first insets the list, so only an
 * offset seen before the reader first drags counts as the top; a drag or a pull to refresh
 * would otherwise be taken for it. A tab opened after launch, such as the inbox, gets no such
 * event, so the offset where the reader's first drag begins stands in: the list rests at its
 * top until then.
 *
 * A restore does not land in one go. It runs before the new section's rows reach the native
 * list, so the old content size clamps it, and when it crosses the point where the large title
 * grows or shrinks, UIKit moves the offset again by the title's height. So the list keeps
 * re-applying the offset as the scroll events and content size come in, until it holds or the
 * reader drags, and remembers no offset for the section until then.
 *
 * Tapping the selected tab scrolls to that same top, as does the tab root's `scrollToTop`. The native tab bar's own scroll to top
 * stops at the inset of the collapsed bar, under the lead, and React Navigation's goes to
 * zero, which is lower still, so the tab layout turns the native one off and no scene uses
 * React Navigation's.
 *
 * Every row key is prefixed with its section, so two sections that key rows the same way, by
 * index, never share a cell, its state or its measured height. Viewability is filtered back to
 * the selected section's rows, with the prefix removed, before it reaches that section.
 *
 * The lead comes first in the header, in the same place for every section, so the banner and
 * the filter stay the same elements across a switch and never remount. Each section's own
 * header is keyed by the section, so one section's search field is never reused for
 * another's.
 *
 * Handlers that FlatList reads only once, such as `onViewableItemsChanged`, are stable here
 * and call through to the selected section's own.
 */
function SharedList({
  registry,
  selectedKey,
  lead,
}: {
  registry: SceneListRegistry;
  selectedKey: string;
  lead: ReactNode;
}) {
  const getSceneList = () => registry.get(selectedKey);

  const sceneList = useSyncExternalStore(registry.subscribe, getSceneList, getSceneList);

  const listRef = useRef<FlatList>(null);

  const sceneListRef = useRef(sceneList);

  const offsets = useRef(new Map<string, number>());

  const topOffset = useRef<number | undefined>(Platform.OS === 'android' ? 0 : undefined);

  const hasDragged = useRef(false);

  const currentOffset = useRef<number | undefined>(undefined);

  const pendingRestore = useRef<{ offset: number; attempts: number } | undefined>(undefined);

  const shownKey = useRef(selectedKey);

  const navigation = useContext(NavigationContext);

  const sceneRef = sceneList?.ref;

  useLayoutEffect(() => {
    sceneListRef.current = sceneList;
  });

  useLayoutEffect(() => {
    assignRef(sceneRef, listRef.current);

    return () => assignRef(sceneRef, null);
  }, [sceneRef]);

  useLayoutEffect(() => {
    if (shownKey.current === selectedKey) {
      return;
    }

    shownKey.current = selectedKey;

    const headedFor = pendingRestore.current?.offset ?? currentOffset.current;

    pendingRestore.current = undefined;

    const offset = offsets.current.get(selectedKey) ?? topOffset.current;

    if (offset !== undefined && offset !== headedFor) {
      pendingRestore.current = { offset, attempts: 0 };

      listRef.current?.scrollToOffset({ offset, animated: false });
    }
  }, [selectedKey]);

  useLayoutEffect(() => {
    registry.scrollToTop = () => {
      if (topOffset.current === undefined) {
        return;
      }

      pendingRestore.current = undefined;

      listRef.current?.scrollToOffset({ offset: topOffset.current, animated: true });
    };

    return () => {
      registry.scrollToTop = undefined;
    };
  }, [registry]);

  useEffect(() => {
    const tabNavigations = [];

    for (let parent = navigation; parent; parent = parent.getParent()) {
      if (parent.getState()?.type === 'tab') {
        tabNavigations.push(parent);
      }
    }

    const unsubscribers = tabNavigations.map((tabNavigation) =>
      // @ts-expect-error `tabPress` exists only on a tab navigator, which this one is
      tabNavigation.addListener('tabPress', (event: { data?: { isPrevented?: boolean } }) => {
        if (!event.data?.isPrevented && navigation?.isFocused()) {
          registry.scrollToTop?.();
        }
      })
    );

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [navigation, registry]);

  /**
   * Moves a restore on from where the list reports it is. A scroll event that misses the
   * offset spends one of a few attempts. A change in content size spends none, since FlatList
   * grows a long section a batch of rows at a time and each batch clamps the offset again.
   */
  const settleRestore = useCallback((y: number | undefined, isScrollEvent: boolean) => {
    const pending = pendingRestore.current;

    if (!pending) {
      return;
    }

    const holds = y !== undefined && Math.abs(y - pending.offset) < 1;

    if (holds || (isScrollEvent && pending.attempts >= MAX_RESTORE_ATTEMPTS)) {
      pendingRestore.current = undefined;

      if (y !== undefined) {
        offsets.current.set(shownKey.current, y);
      }

      return;
    }

    if (isScrollEvent) {
      pending.attempts += 1;
    }

    listRef.current?.scrollToOffset({ offset: pending.offset, animated: false });
  }, []);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { y } = event.nativeEvent.contentOffset;

      if (!hasDragged.current) {
        topOffset.current ??= y;
      }

      currentOffset.current = y;

      if (pendingRestore.current) {
        settleRestore(y, true);
      } else {
        offsets.current.set(shownKey.current, y);
      }

      sceneListRef.current?.onScroll?.(event);
    },
    [settleRestore]
  );

  const handleContentSizeChange = useCallback(
    (width: number, height: number) => {
      settleRestore(currentOffset.current, false);

      sceneListRef.current?.onContentSizeChange?.(width, height);
    },
    [settleRestore]
  );

  const handleScrollBeginDrag = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!hasDragged.current) {
      topOffset.current ??= event.nativeEvent.contentOffset.y;
    }

    hasDragged.current = true;

    pendingRestore.current = undefined;

    sceneListRef.current?.onScrollBeginDrag?.(event);
  }, []);

  const handleViewableItemsChanged = useCallback(
    (info: { viewableItems: ViewToken[]; changed: ViewToken[] }) => {
      const prefix = `${shownKey.current}${KEY_SEPARATOR}`;

      const ownTokens = (tokens: ViewToken[]) =>
        tokens
          .filter((token) => token.key.startsWith(prefix))
          .map((token) => ({ ...token, key: token.key.slice(prefix.length) }));

      const changed = ownTokens(info.changed);

      if (changed.length) {
        sceneListRef.current?.onViewableItemsChanged?.({
          viewableItems: ownTokens(info.viewableItems),
          changed,
        });
      }
    },
    []
  );

  const sceneKeyExtractor = sceneList?.keyExtractor ?? defaultKeyExtractor;

  const keyExtractor = useCallback(
    (item: unknown, index: number) =>
      `${selectedKey}${KEY_SEPARATOR}${sceneKeyExtractor(item, index)}`,
    [selectedKey, sceneKeyExtractor]
  );

  const sceneOnEndReached = sceneList?.onEndReached;

  /**
   * FlatList checks the end as the section's rows arrive, and it knows the offset only from
   * the last scroll event, so until the restore holds it measures the new rows against the old
   * section's offset. Only a check made after that counts.
   */
  const handleEndReached = useCallback(
    (info: { distanceFromEnd: number }) => {
      if (shownKey.current === selectedKey && !pendingRestore.current) {
        sceneOnEndReached?.(info);
      }
    },
    [selectedKey, sceneOnEndReached]
  );

  const { ref: _sceneRef, ListHeaderComponent, ...listProps } = sceneList ?? {};

  return (
    <FlatList
      className="flex-1"
      showsVerticalScrollIndicator={false}
      {...listProps}
      ref={listRef}
      data={sceneList?.data ?? NO_ROWS}
      renderItem={sceneList?.renderItem ?? renderNoRow}
      keyExtractor={keyExtractor}
      contentInsetAdjustmentBehavior="automatic"
      scrollToOverflowEnabled
      onScroll={handleScroll}
      onScrollBeginDrag={handleScrollBeginDrag}
      onContentSizeChange={handleContentSizeChange}
      onEndReached={handleEndReached}
      onViewableItemsChanged={handleViewableItemsChanged}
      ListHeaderComponent={
        <>
          <VStack className="bg-background-0">{lead}</VStack>
          <Fragment key={selectedKey}>{renderHeader(ListHeaderComponent)}</Fragment>
        </>
      }
    />
  );
}
